import {useTranslation} from "react-i18next";
import * as React from "react";
import {useContext, useEffect, useState} from "react";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Typography} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import * as Yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import CloseIcon from "@mui/icons-material/Close";
import FormSelect from "../../../experiments/forms/FormSelect";
import {useInvoiceRepository} from "../../../api/repositories/InvoiceRepository";
import {FormDatePicker} from "../../../../booking/components/fields";
import dayjs from "dayjs";

interface LeasePlanModalInputs {
    dateFrom: null,
    dateTo: null,
    legalEntity: string,
    dealer: string
}

const DownloadLeasePlanModal = (props: any) => {
    const {t} = useTranslation('finance', {keyPrefix: 'invoices'});
    const {t: tg} = useTranslation("translation");
    const {exportLeasePlanFile} = useInvoiceRepository();
    const {globalData: {user}, dispatchGlobalData} = useContext(GlobalContext);

    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`legalEntities.${x.name}`),
        }));

    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`dealers.${x.name}`),
        }));

    const [dealers, setDealers] = useState<any[]>([]);

    const schema = Yup.object().shape({
        dateFrom: Yup.date()
            .nullable()
            .test(
                "testFrom",
                t("filters.errors.invalid-date"),
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.dateTo ||
                        value <= context.parent.dateTo
                    );
                }
            )
            .typeError(t("filters.errors.invalid-date")),
        dateTo: Yup.date()
            .nullable()
            .test("testTo", t("filters.errors.invalid-date"), (value, context) => {
                return (
                    !value ||
                    !context.parent.dateFrom ||
                    value >= context.parent.dateFrom
                );
            })
            .typeError(t("filters.errors.invalid-date")),
        legalEntity: Yup.string().required(t('leasePlan.messages.requiredCheck')),
        dealer: Yup.string().required(t('leasePlan.messages.requiredCheck')),
    });

    const defaultValues = () => {
        return {
            dateFrom: null,
            dateTo: null,
            legalEntity: legalEntityOptions.length == 1 ? legalEntityOptions[0].value : "",
            dealer: dealerOptions.length == 1 ? dealerOptions[0].value : ""
        }
    };


    const {watch, control, reset} = useForm<LeasePlanModalInputs>({
        resolver: yupResolver(schema),
        defaultValues: defaultValues()
    });

    const onSubmit = () => {
        exportLeasePlanFile(watch("dealer"), watch("legalEntity"), watch("dateFrom"), watch("dateTo")).then((it) => {
            const blob = new Blob([it], { type: "application/xml" });
            const url = URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = `eInvoices_${dayjs().format("YYYYMMDD_HHmmss")}.xml`
            a.click();

            URL.revokeObjectURL(url);
        });
    }

    const handleChangeLegalEntities = (value: any) => {
        if (value == '') {
            setDealers([]);
            return;
        }
        let dealers = user?.dealers
            ?.filter(
                (x: any) =>
                    x.legalEntities.some((e: any) => value == e.name) &&
                    x.name.toLowerCase() !== "all"
            )
            .map((x: any) => {
                return {
                    label: tg(`dealers.${x.name}`),
                    value: x.name,
                };
            });
        setDealers(dealers);
    }

    useEffect(() => {
        handleChangeLegalEntities(legalEntityOptions[0].value)
        if (legalEntityOptions.length == 1 && dealers.length == 1) {
            setTimeout(() => {
                handleChangeLegalEntities(legalEntityOptions[0].value)
            });
        }
    }, [legalEntityOptions.length == 1])


    return (
        <>
            <Dialog fullWidth={true} open={props.open} onClose={() => {
                return;
            }}>
                <DialogTitle>
                    <Typography fontWeight={"bold"} color={"primary"}>
                        {t('leasePlan.modal.tileDownload')}
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            props.onClose();
                        }}
                        color={"primary"}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon/>
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{padding: "15px"}} alignItems={"center"}>
                        <Grid item xs={6}>
                            <FormSelect
                                name={"legalEntity"}
                                control={control}
                                props={{
                                    label: tg("shared.legalEntities"),
                                    fullWidth: true,
                                    required: true,
                                    onChange: (e: any) => {
                                        handleChangeLegalEntities(e.target.value)
                                    },
                                }}
                                items={legalEntityOptions.map((option: any) => (
                                    <MenuItem key={option.value}
                                              value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormSelect
                                name={"dealer"}
                                control={control}
                                props={{
                                    label: tg("shared.dealers"),
                                    fullWidth: true,
                                    required: true,
                                    disabled: dealers.length === 0
                                }}
                                items={dealers.map((option: any) => (
                                    <MenuItem key={option.value}
                                              value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <FormDatePicker
                                name="dateFrom"
                                control={control}
                                label={t("leasePlan.modal.dateFrom")}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <FormDatePicker
                                name="dateTo"
                                control={control}
                                label={t("leasePlan.modal.dateTo")}
                                minDate={watch("dateFrom") ?? undefined}
                            />
                        </Grid>

                    </Grid>
                </DialogContent>
                <DialogActions sx={{padding: "10px"}}>
                    <>
                        <Button color={"accent"}
                                onClick={() => {
                                    reset(defaultValues());
                                    props.onClose();
                                }}>{t('leasePlan.modal.buttons.close')}
                        </Button>
                        <Button color={"accent"} disabled={!watch("legalEntity")}
                                onClick={() => {
                                    onSubmit();
                                    reset(defaultValues());
                                    props.onClose();
                                }}>{t('leasePlan.modal.buttons.download')}
                        </Button>
                    </>

                </DialogActions>
            </Dialog>
        </>
    )
}

export default DownloadLeasePlanModal;